import {
  calculateCombustionEfficiency,
  evaluateCombustionTest,
  validateTankSeparation,
  validateFireValveTrip
} from '../../utils/oil/oftecCalculations';

/**
 * Compute OFTEC compliance flags used by the Form and PDF layers.
 * Returns live combustion efficiency and tank separation checks.
 */
export const computeOFTECFlags = (formData) => {
  const combustion = formData?.combustion || {};
  const supplyAndTank = formData?.supplyAndTank || {};

  const doCombustion =
    combustion.combustionTestPerformed === 'Yes' &&
    combustion.o2Pct &&
    combustion.flueTempC;

  const efficiency = doCombustion
    ? calculateCombustionEfficiency({
        o2Pct: combustion.o2Pct,
        co2Pct: combustion.co2Pct,
        flueTempC: combustion.flueTempC,
        ambientTempC: combustion.ambientTempC || 20
      })
    : null;

  const combustionResult = doCombustion
    ? evaluateCombustionTest({
        smokeNumber: combustion.smokeNumber,
        coPpm: combustion.coPpm,
        netEfficiencyPct: efficiency?.netEfficiencyPct || 90
      })
    : null;

  const tankCheck = supplyAndTank.distanceToBuildingM || supplyAndTank.distanceToBoundaryM
    ? validateTankSeparation({
        distanceToBuildingM: supplyAndTank.distanceToBuildingM,
        distanceToBoundaryM: supplyAndTank.distanceToBoundaryM,
        capacityLitres: supplyAndTank.tankCapacityLitres,
        fireScreenFitted: supplyAndTank.fireScreenFitted === 'Yes'
      })
    : null;

  const fireValveCheck = supplyAndTank.fireValveTripC
    ? validateFireValveTrip(supplyAndTank.fireValveTripC)
    : null;

  return {
    efficiency,
    combustionResult,
    tankCheck,
    fireValveCheck
  };
};

export default {
  validate(formData) {
    const errors = {};

    if (!formData.installer?.companyName?.trim()) {
      errors.installer_companyName = 'Company Name is required';
    }
    if (!formData.installer?.oftecNumber?.trim()) {
      errors.installer_oftecNumber = 'OFTEC Registration Number is required';
    }
    if (!formData.installer?.address?.trim()) {
      errors.installer_address = 'Address is required';
    }
    if (!formData.installer?.tel?.trim()) {
      errors.installer_tel = 'Telephone is required';
    }
    if (!formData.installation?.address?.trim()) {
      errors.installation_address = 'Installation Address is required';
    }
    if (!formData.installation?.postCode?.trim()) {
      errors.installation_postCode = 'Post Code is required';
    }
    if (!formData.installation?.customerName?.trim()) {
      errors.installation_customerName = 'Customer Name is required';
    }
    if (!formData.equipment?.applianceType?.trim()) {
      errors.equipment_applianceType = 'Appliance Type is required';
    }
    if (!formData.equipment?.make?.trim()) {
      errors.equipment_make = 'Make is required';
    }
    if (!formData.equipment?.model?.trim()) {
      errors.equipment_model = 'Model is required';
    }
    if (!formData.certification?.installationDate?.trim()) {
      errors.certification_installationDate = 'Installation Date is required';
    }
    if (!formData.certification?.certificateNumber?.trim()) {
      errors.certification_certificateNumber = 'Certificate Number is required';
    }
    if (!formData.certification?.engineerSignature?.trim()) {
      errors.certification_engineerSignature = 'Engineer Signature is required';
    }
    if (!formData.certification?.engineerName?.trim()) {
      errors.certification_engineerName = 'Engineer Name (Print) is required';
    }

    // OFTEC engine validations
    const combustion = formData.combustion || {};
    if (combustion.combustionTestPerformed === 'Yes') {
      if (!combustion.o2Pct || !combustion.flueTempC) {
        errors.combustion_test = 'O₂ and flue gas temperature are required when combustion test performed';
      }
      if (combustion.smokeNumber && Number(combustion.smokeNumber) > 1) {
        errors.combustion_smokeNumber =
          'Smoke number exceeds 1. Appliance requires investigation before the certificate can be issued.';
      }
      if (combustion.coPpm && Number(combustion.coPpm) > 100) {
        errors.combustion_coPpm = 'CO reading exceeds 100 ppm — appliance requires servicing.';
      }
    }

    const tank = formData.supplyAndTank || {};
    if (tank.distanceToBuildingM) {
      const check = validateTankSeparation({
        distanceToBuildingM: tank.distanceToBuildingM,
        distanceToBoundaryM: tank.distanceToBoundaryM,
        capacityLitres: tank.tankCapacityLitres,
        fireScreenFitted: tank.fireScreenFitted === 'Yes'
      });
      if (!check.compliant) {
        errors.supplyAndTank_tankSeparation = check.issues.join(' ');
      }
    }

    if (tank.fireValveTripC) {
      const valve = validateFireValveTrip(tank.fireValveTripC);
      if (!valve.valid) {
        errors.supplyAndTank_fireValve = valve.issues.join(' ');
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};